import React, { useEffect, useState } from 'react';

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  fallbackText?: string;
  loadingText?: string;
  errorText?: string;
  retryCount?: number;
  onError?: (error: Error) => void;
  onLoad?: () => void;
}

/**
 * AuthenticatedImage Component
 * Fetches images from WordPress with basic authentication
 * Useful when WordPress media requires authentication to access
 */
const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({ 
  src, 
  alt, 
  className, 
  style,
  fallbackText = '画像なし',
  loadingText = '読み込み中...',
  errorText = '画像読み込みエラー',
  retryCount = 3,
  onError,
  onLoad
}) => {
  const [imageData, setImageData] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retries, setRetries] = useState(0);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(false);
        
        // Get credentials directly from environment variables
        const username = process.env.REACT_APP_WORDPRESS_USERNAME;
        const password = process.env.REACT_APP_WORDPRESS_PASSWORD;
        
        if (!username || !password) {
          throw new Error('WordPress credentials not configured. Please set REACT_APP_WP_USERNAME and REACT_APP_WP_PASSWORD in your .env file.');
        }
        
        const response = await fetch(src, {
          headers: {
            'Authorization': `Basic ${btoa(`${username}:${password}`)}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setImageData(objectUrl);
        setRetries(0); // Reset retry count on success
        onLoad?.(); // Call onLoad callback
      } catch (err) {
        console.error('Error fetching image:', err);
        const error = err instanceof Error ? err : new Error('Unknown error');
        
        if (retries < retryCount) {
          // Retry with exponential backoff
          const delay = Math.pow(2, retries) * 1000;
          setTimeout(() => {
            setRetries(prev => prev + 1);
          }, delay);
        } else {
          setError(true);
          onError?.(error); // Call onError callback
        }
      } finally {
        setLoading(false);
      }
    };

    if (src) {
      fetchImage();
    }

    // Cleanup function to revoke object URL
    return () => {
      if (imageData) {
        URL.revokeObjectURL(imageData);
      }
    };
  }, [src, retries, retryCount, onLoad, onError]);

  if (loading) {
    return (
      <div 
        className={className}
        style={{
          ...style,
          background: '#f0f0f0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '14px',
          gap: '4px'
        }}
      >
        <div>{loadingText}</div>
        {retries > 0 && (
          <div style={{ fontSize: '12px', color: '#999' }}>
            再試行 {retries}/{retryCount}
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className={className}
        style={{
          ...style,
          background: '#ffebee',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#c62828',
          fontSize: '14px',
          gap: '8px'
        }}
      >
        <div>{errorText}</div>
        <button
          onClick={() => {
            setError(false);
            setRetries(0);
            setLoading(true);
          }}
          style={{
            background: '#c62828',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          再試行
        </button>
      </div>
    );
  }

  return (
    <img 
      src={imageData || ''} 
      alt={alt}
      className={className}
      style={style}
    />
  );
};

export default AuthenticatedImage;
